import { Box } from "@mui/material";
import { Palette } from "lucide-react";
import { lazy, Suspense, useCallback, useEffect, useId, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../../../convex/_generated/api";
import { useResilientQuery } from "@/hooks/useResilientQuery";
import { isConvexConfigured } from "@/lib/convex";
import {
  backgroundScopeFor,
  backgroundSwitcherVisible,
  parseBackgroundPreview,
  withBackgroundParams,
  type PageBackgroundChoice,
  type PageBackgroundKind,
  type PageBackgroundPlan,
} from "@/utils/pageBackgrounds";
import type { TintPalette } from "@/utils/coverTint";
import type { BackgroundReleaseRef } from "./usePageBackgroundPlan";

const BackgroundSwitcherPanel = lazy(() => import("./BackgroundSwitcherPanel"));

export interface BackgroundSwitcherProps {
  artistSlug: string | null | undefined;
  plan: PageBackgroundPlan | null | undefined;
  adMode: boolean;
  kind?: PageBackgroundKind;
  coverTint?: TintPalette | null;
  release?: BackgroundReleaseRef;
  coverUrl?: string | null;
}

export function BackgroundSwitcher({ artistSlug, plan, adMode, kind = "release", coverTint, release, coverUrl }: BackgroundSwitcherProps) {
  const scope = backgroundScopeFor(artistSlug);
  const location = useLocation();
  const navigate = useNavigate();
  const preview = parseBackgroundPreview(location.search);
  const previewActive = Boolean(preview.style || preview.color);
  const viewer = useResilientQuery(api.viewer.me, scope === "site-look" && !adMode && isConvexConfigured ? {} : "skip");
  const isAdmin = viewer.status === "ready" && viewer.value.isAdmin;
  const visible = backgroundSwitcherVisible({ isAdmin, previewActive, adMode, scope });
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const panelId = useId();

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) buttonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close(true);
    };
    const onPointer = (event: PointerEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) close(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open, close]);

  const apply = useCallback(
    (patch: { style?: PageBackgroundChoice | null; color?: string | null }) => {
      navigate({ pathname: location.pathname, search: withBackgroundParams(location.search, patch), hash: location.hash }, { replace: true, preventScrollReset: true });
    },
    [navigate, location.pathname, location.search, location.hash],
  );

  if (!visible) return null;

  return (
    <Box
      ref={wrapperRef}
      className="BackgroundSwitcher"
      sx={{
        position: "fixed",
        zIndex: 40,
        left: { xs: "auto", md: 16 },
        right: { xs: 16, md: "auto" },
        top: { xs: "calc(16px + env(safe-area-inset-top, 0px))", md: "auto" },
        bottom: { xs: "auto", md: "calc(16px + env(safe-area-inset-bottom, 0px))" },
        "& [role='dialog']": {
          left: { xs: "auto", md: 0 },
          right: { xs: 0, md: "auto" },
          top: { xs: 56, md: "auto" },
          bottom: { xs: "auto", md: 56 },
        },
      }}
    >
      {open ? (
        <Suspense fallback={null}>
          <BackgroundSwitcherPanel
            id={panelId}
            kind={kind}
            coverTint={coverTint}
            plan={plan}
            currentChoice={preview.style ?? null}
            currentColor={preview.color ?? null}
            release={release}
            coverUrl={coverUrl}
            onChoose={apply}
          />
        </Suspense>
      ) : null}
      <Box
        component="button"
        ref={buttonRef}
        type="button"
        aria-label="Change background"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((current) => !current)}
        sx={{
          width: 44,
          height: 44,
          display: "grid",
          placeItems: "center",
          padding: 0,
          borderRadius: "50%",
          border: "1px solid color-mix(in srgb, var(--logoColor2) 45%, transparent)",
          backgroundColor: "color-mix(in srgb, var(--backgroundColor) 72%, transparent)",
          color: "var(--logoColor2)",
          backdropFilter: "blur(8px)",
          cursor: "pointer",
          opacity: open ? 1 : 0.72,
          transition: "opacity 0.2s ease, transform 0.2s ease",
          "&:hover": { opacity: 1, transform: "scale(1.04)" },
          "&:focus-visible": { outline: "2px solid var(--logoColor2)", outlineOffset: "3px", opacity: 1 },
          "@media (prefers-reduced-motion: reduce)": { transition: "none", "&:hover": { transform: "none" } },
        }}
      >
        <Palette size={20} aria-hidden="true" />
      </Box>
    </Box>
  );
}

export default BackgroundSwitcher;
